import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  TransitionSource,
} from '../../processFlowchartConfigEditorConstants';
import {
  StaticFlowchartNodeData,
  isStaticTaskSchemaNodeData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {TransitionChange} from '../../processFlowchartConfigEditorUtils';
import {getNodeIdPairFromTransitionSourceKey} from '../../TransitionSourceUtils';
import {getFieldPairKey, getTransitionId} from './transitionKeyUtils';
import {findNodePairByIds, findTaskSchemaNodePair, getPTDVIdFromNode} from './transitionLookupUtils';
import {Transition} from './TransitionTypes';

/**
 * Finds all planned transitions that include the given (sourcePTDVId, targetPTDVId) pair.
 *
 * A source→target pair can be represented by multiple transitions simultaneously —
 * a single-source transition (e.g. p1 → p2) and a merging transition that includes
 * it as a subset (e.g. {p1, p3} → p2). Both should be updated together when the
 * user changes the custom mapping or transition source for the shared pair, so
 * the two transitions stay in sync (this follows mirroring logic that exists elsewhere).
 */
function findPlannedTransitionIdsForPTDVPair(
  sourcePTDVId: string,
  targetPTDVId: string,
  transitions: Record<string, Transition>
): Array<string> {
  const ids: Array<string> = [];
  const singleSourceId = getTransitionId([sourcePTDVId], targetPTDVId);
  const singleSource = transitions[singleSourceId];
  if (singleSource != null && singleSource.isPlanned) {
    ids.push(singleSourceId);
  }
  for (const transition of Object.values(transitions)) {
    if (
      transition.id !== singleSourceId &&
      transition.isPlanned &&
      transition.targetPTDVId === targetPTDVId &&
      transition.sourcePTDVIds.includes(sourcePTDVId)
    ) {
      ids.push(transition.id);
    }
  }
  return ids;
}

export interface CreateTransitionChangeForCustomMappingParams {
  edgeId: string;
  /** Pass null to create a change that removes the custom mapping for this edge's field pair. */
  customMappingData: CustomMappingData | null;
  edges: Array<FlowchartStaticEdgeData>;
  nodes: Array<StaticFlowchartNodeData>;
  transitions: Record<string, Transition>;
}

export function createTransitionChangesForCustomMapping({
  edgeId,
  customMappingData,
  edges,
  nodes,
  transitions,
}: CreateTransitionChangeForCustomMappingParams): Array<TransitionChange> {
  const edge = edges.find((e) => e.id === edgeId);
  if (!edge || edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return [];
  }

  const nodePair = findTaskSchemaNodePair(edge, nodes);
  if (!nodePair) {
    return [];
  }

  const sourcePTDVId = getPTDVIdFromNode(nodePair.sourceNode);
  const targetPTDVId = getPTDVIdFromNode(nodePair.targetNode);
  const transitionIds = findPlannedTransitionIdsForPTDVPair(sourcePTDVId, targetPTDVId, transitions);
  if (transitionIds.length === 0) {
    return [];
  }

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  const fieldPairKey = getFieldPairKey({
    sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });

  return transitionIds.map((transitionId) => ({
    type: 'update',
    id: transitionId,
    changes: {
      customMapping: {fieldPairKey, value: customMappingData},
    },
  }));
}

export interface CreateTransitionChangeForTransitionSourceParams {
  nodeIdPair: string;
  transitionSource: TransitionSource;
  nodes: Array<StaticFlowchartNodeData>;
  transitions: Record<string, Transition>;
}

export function createTransitionChangesForTransitionSource({
  nodeIdPair,
  transitionSource,
  nodes,
  transitions,
}: CreateTransitionChangeForTransitionSourceParams): Array<TransitionChange> {
  const {sourceNodeId, targetNodeId} = getNodeIdPairFromTransitionSourceKey(nodeIdPair);

  const nodePair = findNodePairByIds(sourceNodeId, targetNodeId, nodes);
  if (!nodePair) {
    return [];
  }
  if (!isStaticTaskSchemaNodeData(nodePair.sourceNode) || !isStaticTaskSchemaNodeData(nodePair.targetNode)) {
    return [];
  }

  const sourcePTDVId = getPTDVIdFromNode(nodePair.sourceNode);
  const targetPTDVId = getPTDVIdFromNode(nodePair.targetNode);
  const transitionIds = findPlannedTransitionIdsForPTDVPair(sourcePTDVId, targetPTDVId, transitions);

  return transitionIds.map((transitionId) => ({
    type: 'update',
    id: transitionId,
    changes: {
      transitionSource: {sourcePTDVId, value: transitionSource},
    },
  }));
}
